import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api_client';
import type { Redemption } from '@/entities/reward';
import Button from '@/shared/ui/Button/Button';
import { PackageCheck } from 'lucide-react';
import { formatDate } from '@/shared/utils/format';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------
interface DeliverRedemptionProps {
  redemption: Redemption;
  familyId: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
function DeliverRedemption({ redemption, familyId }: DeliverRedemptionProps): React.ReactElement {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);

  const deliverMutation = useMutation({
    mutationFn: () =>
      post(`/families/${familyId}/redemptions/${redemption.id}/deliver`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['redemptions', familyId] });
    },
  });

  const approvedAt = redemption.reviewedAt ? formatDate(redemption.reviewedAt) : '—';

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-0.5 min-w-0">
          <p className="font-semibold text-slate-900 truncate">{redemption.rewardName}</p>
          <p className="text-sm text-slate-500">
            <span className="font-medium text-slate-700">{redemption.childName}</span>
            {' · '}claimed {approvedAt}
          </p>
        </div>
        <span className="inline-flex items-center rounded-full bg-blue-50 px-3 py-1 text-sm font-semibold text-blue-700 shrink-0">
          {redemption.pointCost.toLocaleString()} pts
        </span>
      </div>

      {deliverMutation.isError && (
        <p role="alert" className="mt-3 rounded-lg bg-danger-50 px-3 py-2 text-sm text-danger-700">
          {deliverMutation.error instanceof Error
            ? deliverMutation.error.message
            : 'Something went wrong. Please try again.'}
        </p>
      )}

      <div className="mt-4">
        {confirming ? (
          <div className="flex items-center gap-3">
            <p className="text-sm text-slate-600 flex-1">
              Mark <span className="font-medium">"{redemption.rewardName}"</span> as delivered to{' '}
              <span className="font-medium">{redemption.childName}</span>?
            </p>
            <div className="flex gap-2 shrink-0">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setConfirming(false)}
                disabled={deliverMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                isLoading={deliverMutation.isPending}
                onClick={() => deliverMutation.mutate()}
                leftIcon={<PackageCheck size={14} aria-hidden="true" />}
                className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 focus-visible:ring-blue-500 disabled:bg-blue-300"
                aria-label={`Confirm delivery of ${redemption.rewardName} to ${redemption.childName}`}
              >
                Confirm delivery
              </Button>
            </div>
          </div>
        ) : (
          <Button
            type="button"
            size="sm"
            onClick={() => setConfirming(true)}
            leftIcon={<PackageCheck size={14} aria-hidden="true" />}
            className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 focus-visible:ring-blue-500"
            aria-label={`Mark ${redemption.rewardName} as delivered to ${redemption.childName}`}
          >
            Mark as Delivered
          </Button>
        )}
      </div>
    </div>
  );
}

export default DeliverRedemption;
